import React, { useCallback, useMemo, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { protectedRoutes } from 'constants/routes'

import CheckIcon from '@mui/icons-material/Check'
import { AgGridReact } from 'ag-grid-react'
import { LoadingOverlay } from 'components/commonparts/index'
import { useGridBase } from 'hooks/useGridBase'
import { mediumFontSize } from 'style'
import 'utils/mst.scss'

import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import type { CompanyList } from 'api-client'
import type { DataListStatus } from 'types/types'

interface DataListProps {
  list: CompanyList
  isLoading: boolean
  listStatus: DataListStatus
  onListStatusChange: (status: DataListStatus) => void
  gridRef?: React.RefObject<AgGridReact>
  findSearchValue?: string
  onFindChanged?: (event: any) => void
  isSearchVisible?: boolean
}

// Custom cell renderer for delete flag
function DeleteFlagRenderer(params: ICellRendererParams) {
  const { value } = params
  if (!value) return ''
  return <CheckIcon />
}

const getCommonAgGridStyles = (isSearchVisible: boolean) => ({
  container: {
    width: '100%',
    height: `calc(100vh - ${110 + (isSearchVisible ? 40 : 0)}px)`,
    fontSize: mediumFontSize,
  },
  cellStyle: { fontSize: mediumFontSize },
  headerClass: 'ag-header-cell-custom',
})

function DataList({ list, isLoading, listStatus, onListStatusChange, gridRef: externalGridRef, findSearchValue: externalFindSearchValue, onFindChanged: externalOnFindChanged, isSearchVisible = false }: DataListProps) {
  const navigate = useNavigate()
  const internalGridRef = useRef<AgGridReact>(null)
  const gridRef = externalGridRef || internalGridRef
  
  const {
    onGridReady,
    localeText,
    autoSizeStrategy,
    defaultColDef,
  } = useGridBase({
    gridRef,
    isLoading,
    storageKey: 'gridColumnState:/company-list',
  })

  // Enhanced default column definition using common styles
  const enhancedDefaultColDef = useMemo(() => ({
    ...defaultColDef,
    cellStyle: getCommonAgGridStyles(isSearchVisible).cellStyle,
    headerClass: getCommonAgGridStyles(isSearchVisible).headerClass
  }), [defaultColDef])

  const columnDefs = useMemo<ColDef[]>(() => [
    {
      field: 'companyId',
      headerName: 'PSI企業ID',
      width: 200,
      cellClass: 'link-cell',
      onCellClicked: (params) => {
        // Only redirect if clicking on the text
        const { event } = params;
        const target = event?.target as HTMLElement;
        
        if (target && target.classList.contains('ag-cell')) {
          const rect = target.getBoundingClientRect();
          const mouseEvent = event as MouseEvent;
          const clickX = mouseEvent.clientX;
          
          // Only redirect if clicking on the left half of the cell (where the text is)
          if (clickX - rect.left < rect.width / 2) {
            navigate(`/${protectedRoutes.CompanyDetail.path}?company_id=${params.value || ''}`)
          }
        }
      },
    },
    {
      headerName: '企業名',
      field: 'companyName',
      width: 200,
    },
    {
      headerName: '会社法人等番号（登記所）',
      field: 'companyHojinNo',
      width: 200,
    },
    {
      headerName: '本社住所',
      field: 'companyAddress',
      width: 300,
    },
    {
      headerName: 'GS1事業者コード',
      field: 'companyGln',
      width: 200,
    },
    {
      headerName: '業態',
      field: 'businessCategoryName',
      width: 200,
    },
    {
      headerName: 'テナントID',
      field: 'tenantId',
      width: 200,
    },
    {
      headerName: '無効',
      field: 'deleteFlg',
      cellRenderer: DeleteFlagRenderer,
      width: 100,
    },
    {
      field: 'companyId',
      width: 200,
      headerName: 'ユーザー一覧',
      cellClass: 'link-cell',
      onCellClicked: (params) => {
        // Only redirect if clicking on the text
        const { event } = params;
        const target = event?.target as HTMLElement;
        
        if (target && target.classList.contains('ag-cell')) {
          const rect = target.getBoundingClientRect();
          const mouseEvent = event as MouseEvent;
          const clickX = mouseEvent.clientX;
          
          // Only redirect if clicking on the left half of the cell (where the text is)
          if (clickX - rect.left < rect.width / 2) {
            navigate(`/${protectedRoutes.UserList.path}?company_id=${params.value || ''}`)
          }
        }
      },
    },
    {
      headerName: 'ログインURL',
      width: 400,
      field: 'loginUrl',
      valueGetter: (params) => {
        if (!params.data) return ''
        return `${new URL(document.URL).origin}/login${params.data.tenantId ? `?tenantId=${params.data.tenantId}` : ''}`
      },
    },
  ], [navigate])

  // ページネーション変更時のAPIコール処理
  const onPaginationChanged = useCallback(() => {
    if (!gridRef.current?.api) return
    
    const currentPage = gridRef.current.api.paginationGetCurrentPage()
    const pageSize = gridRef.current.api.paginationGetPageSize()
    
    if (currentPage !== listStatus.paginationModel.page || pageSize !== listStatus.paginationModel.pageSize) {
      const targetPage = pageSize !== listStatus.paginationModel.pageSize ? 0 : currentPage
      
      onListStatusChange({
        sortModel: listStatus.sortModel,
        paginationModel: { page: targetPage, pageSize }
      })
    }
  }, [listStatus.sortModel, listStatus.paginationModel.page, listStatus.paginationModel.pageSize, onListStatusChange])

  // ローディング中はページネーションパネルを非表示にして不正確な情報を防ぐ
  useEffect(() => {
    const timer = setTimeout(() => {
      const paginationPanel = document.querySelector('.ag-paging-panel')
      if (paginationPanel) {
        (paginationPanel as HTMLElement).style.visibility = isLoading ? 'hidden' : 'visible'
      }
    }, 10)
    return () => clearTimeout(timer)
  }, [isLoading])

  return (
    <div style={getCommonAgGridStyles(isSearchVisible).container} className="ag-theme-balham">
      <AgGridReact
        ref={gridRef}
        rowData={list.list}
        columnDefs={columnDefs}
        defaultColDef={enhancedDefaultColDef}
        onGridReady={onGridReady}
        localeText={localeText}
        autoSizeStrategy={autoSizeStrategy}
        loadingOverlayComponent={LoadingOverlay}
        pagination
        paginationPageSize={listStatus.paginationModel.pageSize}
        findSearchValue={externalFindSearchValue}
        onFindChanged={externalOnFindChanged}
        paginationPageSizeSelector={[100, 300, 500]}
        onPaginationChanged={onPaginationChanged}
        getRowId={(params) => params.data.companyId}
        suppressScrollOnNewData
        rowHeight={40}
        headerHeight={42}
        paginationAutoPageSize={false}
        suppressColumnVirtualisation
      />
    </div>
  )
}

export default DataList
